import {
  pickSeasonStandingLeader,
  seasonStandingDisplayPoints,
} from '@multirx/shared/game/seasonStandingsSort.js';
import type {
  ActiveSeasonView,
  GlobalActiveSeasonView,
  SeasonStatus,
} from '@multirx/shared/types/championship.js';
import { DIVISIONS, type DivisionId } from '@multirx/shared/types/division.js';
import { getOwnDriverId } from '../auth/ownDriverLabel.js';
import { formatDriverDisplayName } from '@multirx/shared/game/driverDisplayLabel.js';
import { formatGlobalMeetingProgress } from '../championship/seasonMeetingLabels.js';
import { standingsPointsCellHtml } from '../standings/standingsTableMarkup.js';

export type SeasonTeaserLeader = {
  division: DivisionId;
  shortLabel: string;
  driverId: number | null;
  driverName: string | null;
  points: number | null;
  rank: number | null;
  seasonId: number | null;
};

export type SeasonTeaserModel = {
  status: 'none' | SeasonStatus;
  progressLabel: string;
  leaders: SeasonTeaserLeader[];
};

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

function leaderFromView(
  view: ActiveSeasonView | undefined
): Omit<SeasonTeaserLeader, 'division' | 'shortLabel'> {
  if (!view) {
    return {
      driverId: null,
      driverName: null,
      points: null,
      rank: null,
      seasonId: null,
    };
  }
  const top = pickSeasonStandingLeader(view.standings);
  if (!top) {
    return {
      driverId: null,
      driverName: null,
      points: null,
      rank: null,
      seasonId: view.season.id,
    };
  }
  return {
    driverId: top.driverId,
    driverName: top.driverName,
    points: seasonStandingDisplayPoints(top),
    rank: top.rank,
    seasonId: view.season.id,
  };
}

export function buildSeasonTeaserModel(
  globalView: GlobalActiveSeasonView | null
): SeasonTeaserModel {
  if (!globalView) {
    return {
      status: 'none',
      progressLabel: 'Aucune saison en cours',
      leaders: DIVISIONS.map((d) => ({
        division: d.id,
        shortLabel: d.shortLabel,
        driverId: null,
        driverName: null,
        points: null,
        rank: null,
        seasonId: null,
      })),
    };
  }

  const indices = Object.values(globalView.divisions)
    .filter((v): v is ActiveSeasonView => v != null)
    .map((v) => v.season.currentMeetingIndex);

  let progressLabel: string;
  if (globalView.status === 'completed') {
    progressLabel = 'Dernière campagne terminée';
  } else if (globalView.status === 'registration') {
    progressLabel = 'Inscriptions ouvertes';
  } else {
    progressLabel = formatGlobalMeetingProgress(indices, globalView.meetingCount);
  }

  return {
    status: globalView.status,
    progressLabel,
    leaders: DIVISIONS.map((d) => {
      const lead = leaderFromView(globalView.divisions[d.id]);
      return {
        division: d.id,
        shortLabel: d.shortLabel,
        ...lead,
      };
    }),
  };
}

function leadersTableHtml(model: SeasonTeaserModel): string {
  const rows = model.leaders
    .map((lead) => {
      const hasDriver = lead.driverId != null && lead.driverName != null && lead.rank != null;
      const points = lead.points ?? 0;
      // rank 0 : pas de pastille or/argent (jaune sur fond rouge) — garde l’accent marque.
      const ptsCell = hasDriver
        ? standingsPointsCellHtml(points, 0, {
            seasonId: lead.seasonId ?? undefined,
            driverId: lead.driverId!,
            bucket: 'championship',
          })
        : `<span class="dashboard-season-teaser__pts dashboard-season-teaser__pts--empty">—</span>`;

      const rowAttrs = hasDriver
        ? ` class="standings-driver-row dashboard-season-teaser__tr" data-driver-id="${lead.driverId}" data-driver-name="${escapeAttr(lead.driverName!)}" data-standings-rank="${lead.rank}" data-standings-points="${points}" data-division="${lead.division}"${lead.seasonId != null ? ` data-season-id="${lead.seasonId}"` : ''} tabindex="0"`
        : ` class="dashboard-season-teaser__tr dashboard-season-teaser__tr--empty"`;

      const displayName = hasDriver
        ? formatDriverDisplayName(lead.driverName!, lead.driverId, getOwnDriverId())
        : '';
      return `<tr${rowAttrs}>
        <td class="dashboard-season-teaser__col-div">${escapeHtml(lead.shortLabel)}</td>
        <td class="dashboard-season-teaser__col-driver">${hasDriver ? escapeHtml(displayName) : '—'}</td>
        <td class="dashboard-season-teaser__col-pts">${ptsCell}</td>
      </tr>`;
    })
    .join('');

  return `<div class="dashboard-season-teaser__table-wrap">
    <table class="dashboard-season-teaser-table" role="table" aria-label="Leaders par division">
      <tbody>${rows}</tbody>
    </table>
  </div>`;
}

/** Carte teaser (pas un lien global : tooltips + CTA séparés). */
export function seasonTeaserCardHtml(model: SeasonTeaserModel): string {
  return `<div class="card-glow-wrap card-glow-wrap--season-teaser">
    <div class="card-glow card-glow--preview dashboard-season-teaser-card">
      <p class="heading">Saisons</p>
      <p class="dashboard-season-teaser__progress">${escapeHtml(model.progressLabel)}</p>
      ${leadersTableHtml(model)}
      <a href="#saisons" class="dashboard-season-teaser__cta">Voir la saison →</a>
    </div>
  </div>`;
}
